import { ReactNode, useMemo } from 'react'

import { Event } from '@tribeplatform/gql-client/types'
import { SvgIcon, SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { useBrowserTimeZone } from '../../common/index.js'
import { T } from '../../i18n/components/T.js'
import { Link } from '../../Link/Link.js'
import { VirtualLinkIcon } from '../Components/VirtualLinkIcon.js'
import { VirtualLinkProvider } from '../types.js'
import { getEventFormattedDate } from '../utils.js'

import { hasRegisteredForEvent } from './utils.js'

interface Props {
  event: Event
}

export const BasicDetails = ({ event }: Props) => {
  const browserTimeZone = useBrowserTimeZone()

  const { date, time } = useMemo(
    () =>
      getEventFormattedDate({
        startTime: event.startTime,
        endTime: event.endTime,
      }),
    [event.startTime, event.endTime],
  )

  return (
    <div className="flex flex-col gap-y-3">
      <DetailItem
        icon="calendar"
        title={date}
        value={
          <Text className="text-sm text-content-subdued">
            {/* eslint-disable-next-line react/jsx-no-literals */}
            {time} ({browserTimeZone})
          </Text>
        }
      />
      <PhysicalLocations event={event} />
      <VirtualLocations event={event} />
    </div>
  )
}

const PhysicalLocations = ({ event }: { event: Event }) => {
  const location = event.physicalLocations?.at(0)

  if (!location) {
    return null
  }

  return (
    <div className="flex flex-col gap-y-3">
      {location && (
        <DetailItem
          icon="location-marker"
          title={location.displayName}
          value={
            <div className="flex flex-col gap-y-2">
              <Text className="text-sm text-content-subdued">
                {location.formattedAddress}
              </Text>
            </div>
          }
        />
      )}
    </div>
  )
}

const VirtualLocations = ({ event }: { event: Event }) => {
  const { virtualLocations } = event

  if (!virtualLocations?.length || !hasRegisteredForEvent(event)) {
    return null
  }

  return (
    <div className="flex flex-col gap-y-3">
      <DetailItem
        icon="link"
        title={
          <T
            id="Event.BasicDetails.VirtualLocation"
            defaultMessage="Virtual links"
          />
        }
        value={
          <div className="flex flex-col gap-y-1">
            {virtualLocations.map(location => (
              <Link
                key={location.id}
                className="text-sm text-action-primary flex gap-x-1"
                href={location.url}
                target="_blank"
              >
                <VirtualLinkIcon
                  provider={location.provider as VirtualLinkProvider}
                  className="size-4 shrink-0"
                />
                <span className="line-clamp-2 break-all min-w-0">
                  {location.url}
                </span>
              </Link>
            ))}
          </div>
        }
      />
    </div>
  )
}

interface DetailItemProps {
  icon: SvgIconName
  title: ReactNode
  value: ReactNode
}

const DetailItem = ({ icon, title, value }: DetailItemProps) => {
  return (
    <div className="flex gap-x-2">
      <div className="bg-surface-subdued rounded-base flex items-center justify-center size-12 shrink-0 ">
        <SvgIcon name={icon} size="xl" />
      </div>
      <div className="flex flex-col gap-y-1 w-full min-w-0">
        <Text className="text-sm break-words">{title}</Text>
        <div className="break-words">{value}</div>
      </div>
    </div>
  )
}
